/**
 * components/charts/TrailChart — the "how the stop is trailing" mini price/stop chart for
 * the EX.8 position-management view. Two series over the review timeline:
 *   • mark price   — a thin smooth accent line (where price actually went), and
 *   • protective stop — a STEPPED amber line (the stop holds, then ratchets in discrete
 *     moves), which is the honest shape of a place-new-then-cancel-old trail (invariant §1.2).
 * A recessive dashed reference line marks the entry. Built to the dataviz skill: two series
 * → a legend is always present (identity never color-alone), thin marks, a recessive grid,
 * selective end dots, and a crosshair+tooltip hover layer. Accent (blue) vs warning (amber)
 * is a high-separation CVD-safe pair from the terminal's validated token ramp.
 *
 * Pure presentational: the caller assembles the point series from the position's review
 * snapshots; this only renders SVG.
 */
import { useMemo, useRef, useState } from 'react';

import { num } from '../../lib/format';

export interface TrailPoint {
  /** Sample time (ms) — the review's createdAt (or the open time for the seed point). */
  readonly at: number;
  /** Mark price at the sample, or null if unknown (the mark line skips it). */
  readonly mark: number | null;
  /** Protective stop trigger at the sample, or null if none was armed. */
  readonly stop: number | null;
}

interface TrailChartProps {
  readonly points: readonly TrailPoint[];
  /** Entry price — drawn as a recessive dashed reference line. */
  readonly entryPrice?: number | null;
  readonly height?: number;
  readonly caption?: string;
}

const VIEW_W = 560;
const PAD = { l: 10, r: 58, t: 14, b: 22 } as const;

export function TrailChart({ points, entryPrice, height = 180, caption }: TrailChartProps): JSX.Element {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [hover, setHover] = useState<number | null>(null);

  const geo = useMemo(() => {
    const plotW = VIEW_W - PAD.l - PAD.r;
    const plotH = height - PAD.t - PAD.b;
    if (points.length < 2) return null;

    const vals: number[] = [];
    for (const p of points) {
      if (p.mark != null) vals.push(p.mark);
      if (p.stop != null) vals.push(p.stop);
    }
    if (entryPrice != null) vals.push(entryPrice);
    if (vals.length < 2) return null;

    const ts = points.map((p) => p.at);
    const minT = Math.min(...ts);
    const maxT = Math.max(...ts);
    const minV = Math.min(...vals);
    const maxV = Math.max(...vals);
    const padV = (maxV - minV) * 0.14 || maxV * 0.01 || 1;
    const y0 = minV - padV;
    const y1 = maxV + padV;

    const x = (t: number): number => PAD.l + ((t - minT) / (maxT - minT || 1)) * plotW;
    const y = (v: number): number => PAD.t + (1 - (v - y0) / (y1 - y0 || 1)) * plotH;

    const xy = points.map((p) => ({ px: x(p.at), mark: p.mark, stop: p.stop, at: p.at }));

    // Mark: a plain line across the samples that carry a mark.
    const markPts = xy.filter((p) => p.mark != null);
    const markLine = markPts
      .map((p, i) => `${i === 0 ? 'M' : 'L'}${p.px.toFixed(1)} ${y(p.mark as number).toFixed(1)}`)
      .join(' ');

    // Stop: a STEPPED line — the stop holds at its level, then jumps at each review.
    const stopPts = xy.filter((p) => p.stop != null);
    let stopLine = '';
    stopPts.forEach((p, i) => {
      const py = y(p.stop as number).toFixed(1);
      if (i === 0) {
        stopLine += `M${p.px.toFixed(1)} ${py}`;
      } else {
        const prevY = y(stopPts[i - 1]!.stop as number).toFixed(1);
        stopLine += ` L${p.px.toFixed(1)} ${prevY} L${p.px.toFixed(1)} ${py}`;
      }
    });

    const gridVals = [y1, (y1 + y0) / 2, y0];
    const grid = gridVals.map((v) => ({ v, gy: y(v) }));
    const entryY = entryPrice != null ? y(entryPrice) : null;

    return { plotW, plotH, x, y, xy, markPts, stopPts, markLine, stopLine, grid, entryY, minT, maxT };
  }, [points, entryPrice, height]);

  if (!geo) {
    return (
      <div className="chart">
        <div className="state" style={{ minHeight: height }}>
          <span className="muted">Not enough trail history to plot yet.</span>
        </div>
      </div>
    );
  }

  const lastMark = geo.markPts[geo.markPts.length - 1];
  const lastStop = geo.stopPts[geo.stopPts.length - 1];
  const hovered = hover != null ? geo.xy[hover] : null;

  const onMove = (e: React.PointerEvent<SVGSVGElement>): void => {
    const rect = e.currentTarget.getBoundingClientRect();
    const svgX = ((e.clientX - rect.left) / rect.width) * VIEW_W;
    let best = 0;
    let bestD = Infinity;
    geo.xy.forEach((p, i) => {
      const d = Math.abs(p.px - svgX);
      if (d < bestD) { bestD = d; best = i; }
    });
    setHover(best);
  };

  const wrapW = wrapRef.current?.clientWidth ?? VIEW_W;
  const scale = wrapW / VIEW_W;
  const label =
    caption ??
    `Mark price and protective stop over the review timeline${entryPrice != null ? `, entry ${num(entryPrice)}` : ''}`;

  return (
    <div className="chart trail-chart" ref={wrapRef} style={{ position: 'relative' }}>
      <svg
        viewBox={`0 0 ${VIEW_W} ${height}`}
        role="img"
        aria-label={label}
        preserveAspectRatio="none"
        onPointerMove={onMove}
        onPointerLeave={() => setHover(null)}
      >
        {/* recessive grid + right-side value labels */}
        {geo.grid.map((g, i) => (
          <g key={i}>
            <line x1={PAD.l} x2={VIEW_W - PAD.r} y1={g.gy} y2={g.gy} stroke="var(--viz-grid)" strokeWidth={1} />
            <text x={VIEW_W - PAD.r + 6} y={g.gy + 3} fontSize={10} fill="var(--text-muted)" fontFamily="var(--font-mono)">
              {num(g.v, 0)}
            </text>
          </g>
        ))}

        {/* entry reference */}
        {geo.entryY != null && (
          <g>
            <line
              x1={PAD.l} x2={VIEW_W - PAD.r} y1={geo.entryY} y2={geo.entryY}
              stroke="var(--viz-axis)" strokeWidth={1} strokeDasharray="4 4"
            />
            <text x={PAD.l + 2} y={geo.entryY - 4} fontSize={10} fill="var(--text-muted)">entry</text>
          </g>
        )}

        {/* stop (stepped, amber) then mark (accent) so mark reads on top */}
        {geo.stopLine && (
          <path d={geo.stopLine} fill="none" stroke="var(--warning)" strokeWidth={1.75} strokeLinejoin="round" strokeLinecap="round" />
        )}
        {geo.markLine && (
          <path d={geo.markLine} fill="none" stroke="var(--accent)" strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
        )}

        {/* crosshair on hover */}
        {hovered && (
          <g className="chart__crosshair">
            <line x1={hovered.px} x2={hovered.px} y1={PAD.t} y2={height - PAD.b} stroke="var(--viz-axis)" strokeWidth={1} strokeDasharray="3 3" />
            {hovered.mark != null && <circle cx={hovered.px} cy={geo.y(hovered.mark)} r={3.5} fill="var(--accent)" stroke="var(--viz-surface)" strokeWidth={2} />}
            {hovered.stop != null && <circle cx={hovered.px} cy={geo.y(hovered.stop)} r={3.5} fill="var(--warning)" stroke="var(--viz-surface)" strokeWidth={2} />}
          </g>
        )}

        {/* selective end dots */}
        {lastMark?.mark != null && <circle cx={lastMark.px} cy={geo.y(lastMark.mark)} r={3.2} fill="var(--accent)" stroke="var(--viz-surface)" strokeWidth={2} />}
        {lastStop?.stop != null && <circle cx={lastStop.px} cy={geo.y(lastStop.stop)} r={3.2} fill="var(--warning)" stroke="var(--viz-surface)" strokeWidth={2} />}

        {/* x-axis endpoints */}
        <text x={PAD.l} y={height - 6} fontSize={10} fill="var(--text-muted)">{shortTime(geo.minT)}</text>
        <text x={VIEW_W - PAD.r} y={height - 6} fontSize={10} fill="var(--text-muted)" textAnchor="end">{shortTime(geo.maxT)}</text>
      </svg>

      {hovered && (
        <div className="chart-tooltip" style={{ left: hovered.px * scale, top: PAD.t * scale }}>
          <div className="num"><span className="trail-dot trail-dot--mark" aria-hidden /> mark {hovered.mark != null ? num(hovered.mark) : '—'}</div>
          <div className="num"><span className="trail-dot trail-dot--stop" aria-hidden /> stop {hovered.stop != null ? num(hovered.stop) : '—'}</div>
          <span className="muted">{fullTime(hovered.at)}</span>
        </div>
      )}

      <div className="chart__caption">
        <span className="chart__legend-swatch" style={{ background: 'var(--accent)' }} aria-hidden />
        <span>Mark price</span>
        <span className="chart__legend-swatch" style={{ background: 'var(--warning)', marginLeft: 'var(--sp-3)' }} aria-hidden />
        <span>Protective stop</span>
      </div>
    </div>
  );
}

function shortTime(t: number): string {
  return new Date(t).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'UTC' });
}
function fullTime(t: number): string {
  return new Date(t).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'UTC' });
}
